import platform from '@play-co/gcinstant';
import { Native } from 'src/replicant/native';
import StateObserver from 'src/StateObserver';
import features from 'src/replicant/ruleset/features';
import { duration } from 'src/replicant/utils/duration';
import { assertNever } from '../../replicant/utils';

export function isNativePromoAvailable() {
  if (process.env.PLATFORM !== 'line') return false;

  const { level } = StateObserver.getState().user;
  return isNativeAppAvailable() && level.current >= Native.ruleset.enabledLevel;
}

export const isNativeAppAvailable = () => {
  const { user } = StateObserver.getState();
  switch (platform.osType) {
    case 'IOS':
      return features(user).nativeIos;
    case 'ANDROID':
      return features(user).nativeAndroid;
    default:
      return false;
  }
};

/**
 * The word GUESS is used here for a purpose: we can't be 100% sure that a user has the native app,
 * but we're trying to "predict" it:
 * - check if replicant has data about native app installed,
 * - check if the native app was launched recently
 */
export function guessIsNativeAppInstalled(platform: 'IOS' | 'ANDROID') {
  const { native } = StateObserver.getState().user;
  let appLastLaunched: number;
  switch (platform) {
    case 'IOS':
      appLastLaunched = native.appleLastLaunched;
      break;
    case 'ANDROID':
      appLastLaunched = native.googleLastLaunched;
      break;
    default:
      assertNever(platform);
  }

  return StateObserver.now() - appLastLaunched < duration({ days: 7 });
}

/**
 * The word GUESS is used here for a purpose: we can't be 100% sure that a user has the native app,
 * but we're trying to "predict" it:
 * - check if replicant has data about native app installed,
 * - check if the native app was launched recently
 */
export function guessInstalledNativeAppPlatform():
  | 'ANDROID_APP'
  | 'IOS_APP'
  | null {
  // Is IOS app installed
  const iosInstalled =
    platform.osType === 'IOS' && guessIsNativeAppInstalled('IOS');
  if (iosInstalled) {
    return 'IOS_APP';
  }

  // Is Android app installed
  const androidInstalled =
    platform.osType === 'ANDROID' && guessIsNativeAppInstalled('ANDROID');
  if (androidInstalled) {
    return 'ANDROID_APP';
  }

  return null;
}

export function hasLaunchedNativeAppPlatform() {
  const { native } = StateObserver.getState().user;
  return native.appleLastLaunched > 0 || native.googleLastLaunched > 0;
}
/**
 * Get link on store depend on osType
 */
export function getNativeStoreLink() {
  switch (platform.osType) {
    case 'IOS':
      return Native.ruleset.appStoreURL;
    case 'ANDROID':
      return Native.ruleset.googlePlayURL;
    default:
      assertNever(platform.osType as never);
  }
}
